/**
 * Icons.
 *
 * Animated lucide icons from animateicons.in, vendored into src/app/icons with
 * that project's CLI rather than taken from its npm package. The package ships
 * all 509 icons in one module that no bundler will tree-shake — it put 900 KB of
 * unused glyphs in the bundle. Copying the twenty-nine this app uses costs a
 * directory of generated files and gets the whole set down to a few kilobytes.
 *
 * Either way they are bundled rather than fetched: the Content-Security-Policy
 * in src/worker/security.ts allows no external origin, and an icon set pulled
 * from a CDN would be a third-party script beside a form that handles a
 * customer's model key.
 *
 * To add one: `npx animateicons add lu-<name> -d src/app/icons`, then export it
 * below. The files are left exactly as the CLI writes them.
 *
 * Each icon animates on its own hover by default. Attaching a ref switches it to
 * imperative control, which is what `IconHover` uses: a nav row or a button
 * drives the icon from the whole row's hover, so the animation plays when the
 * pointer reaches the label rather than only the 16 pixels of glyph.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ComponentPropsWithoutRef,
  type ElementType,
  type ReactNode,
} from 'react';

export { ArrowLeftIcon } from './icons/arrow-left';
export { ArrowRightIcon } from './icons/arrow-right';
export { BookOpenIcon } from './icons/book-open';
export { CheckIcon } from './icons/check';
export { CircleCheckIcon } from './icons/circle-check';
export { ClockIcon } from './icons/clock';
export { CopyIcon } from './icons/copy';
export { DownloadIcon } from './icons/download';
export { ExternalLinkIcon } from './icons/external-link';
export { EyeIcon } from './icons/eye';
export { EyeOffIcon } from './icons/eye-off';
export { FileSpreadsheetIcon } from './icons/file-spreadsheet';
export { FileTextIcon } from './icons/file-text';
export { GlobeIcon } from './icons/globe';
export { KeyIcon } from './icons/key';
export { LoaderCircleIcon } from './icons/loader-circle';
export { LockIcon } from './icons/lock';
export { LogOutIcon } from './icons/log-out';
export { MoonIcon } from './icons/moon';
export { PencilIcon } from './icons/pencil';
export { PlusIcon } from './icons/plus';
export { QuoteIcon } from './icons/quote';
export { RefreshCwIcon } from './icons/refresh-cw';
export { SearchIcon } from './icons/search';
export { SettingsIcon } from './icons/settings';
export { ShieldCheckIcon } from './icons/shield-check';
export { SparklesIcon } from './icons/sparkles';
export { SunIcon } from './icons/sun';
export { Trash2Icon } from './icons/trash-2';
export { TriangleAlertIcon } from './icons/triangle-alert';
export { UploadIcon } from './icons/upload';
export { XIcon } from './icons/x';

export interface IconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

export interface IconProps {
  size?: number;
  duration?: number;
  isAnimated?: boolean;
  color?: string;
  className?: string;
}

/** The shape every icon in the set shares, narrowed to what this app passes. */
export type AnimatedIcon = ElementType<IconProps & { ref?: React.Ref<IconHandle> }>;

interface HoverGroup {
  register: (handle: IconHandle) => () => void;
}

const HoverContext = createContext<HoverGroup | null>(null);

/**
 * Wraps a row, button or card so that hovering anywhere in it plays whichever
 * icons it contains.
 */
export function IconHover<T extends ElementType = 'div'>({
  as,
  children,
  ...rest
}: { as?: T; children: ReactNode } & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children'>) {
  const Tag = (as ?? 'div') as ElementType;
  const handles = useRef(new Set<IconHandle>());

  const register = useCallback((handle: IconHandle) => {
    handles.current.add(handle);
    return () => {
      handles.current.delete(handle);
    };
  }, []);

  const props = rest as Record<string, unknown> & {
    onMouseEnter?: (e: React.MouseEvent) => void;
    onMouseLeave?: (e: React.MouseEvent) => void;
    onFocus?: (e: React.FocusEvent) => void;
    onBlur?: (e: React.FocusEvent) => void;
  };

  const start = () => handles.current.forEach((h) => h.startAnimation());
  const stop = () => handles.current.forEach((h) => h.stopAnimation());

  return (
    <HoverContext.Provider value={{ register }}>
      <Tag
        {...props}
        onMouseEnter={(e: React.MouseEvent) => {
          start();
          props.onMouseEnter?.(e);
        }}
        onMouseLeave={(e: React.MouseEvent) => {
          stop();
          props.onMouseLeave?.(e);
        }}
        // Keyboard users get the same feedback as pointer users.
        onFocus={(e: React.FocusEvent) => {
          start();
          props.onFocus?.(e);
        }}
        onBlur={(e: React.FocusEvent) => {
          stop();
          props.onBlur?.(e);
        }}
      >
        {children}
      </Tag>
    </HoverContext.Provider>
  );
}

/**
 * Renders one icon. Inside an `IconHover` it is driven by the group; on its own
 * it keeps the library's default hover behaviour.
 */
export function Icon({ icon: Component, size = 16, ...rest }: { icon: AnimatedIcon } & IconProps) {
  const group = useContext(HoverContext);
  const ref = useRef<IconHandle>(null);

  useEffect(() => {
    if (!group || !ref.current) return;
    return group.register(ref.current);
  }, [group]);

  if (!group) return <Component size={size} {...rest} />;
  return <Component ref={ref} size={size} {...rest} />;
}

/**
 * The wordmark: a quotation mark inside a seal. The product is a citation you
 * can hand to someone else, which is the whole of it in one glyph.
 */
export function Wordmark({ size = 22 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2.5 20 6v5.5c0 4.6-3.2 8.3-8 9.9-4.8-1.6-8-5.3-8-9.9V6z" />
      <path d="M9.4 13.2c-1.1 0-1.9-.8-1.9-1.9 0-1.6 1.2-3 2.9-3.4" />
      <path d="M15.1 13.2c-1.1 0-1.9-.8-1.9-1.9 0-1.6 1.2-3 2.9-3.4" />
    </svg>
  );
}
